/*
========================================================================
Canvas Utilities

Helper functions and configuration for the room canvas rendering.
All spacing values are defined in millimeters (mm) for consistency
and converted to pixels at render time using gridStep.
========================================================================
*/

/*
------------------------------------------------------------------------
Product spacing configuration.

All values are in millimeters (mm).
- minSpacing: Minimum allowed spacing between products
- maxSpacing: Maximum allowed spacing between products
- preferredSpacing: Fixed spacing (bypasses dynamic calculation)
- stackThreshold: Fraction of available space (0-1) within which products
                  should be kept in a single row/column (default 0.8 = 80%)
------------------------------------------------------------------------
*/
const PRODUCT_SPACING_CONFIG = {
	default: {
		minSpacing: 200, // mm
		maxSpacing: 400, // mm
		preferredSpacing: null, // null = use dynamic calculation
		stackThreshold: 0.7, // Keep in single row if total width <= 70% of available space
	},
	// Linea: fixed 200mm spacing
	LN: {
		preferredSpacing: 200,
		stackThreshold: 0.6, // More conservative for elongated products
	},
	// Blade: fixed 200mm spacing
	BL: {
		preferredSpacing: 200,
		stackThreshold: 0.6, // More conservative for elongated products
	},
	// Add more product-specific spacing here as needed
	// Example:
	// XX: {
	//     minSpacing: 150,
	//     maxSpacing: 400,
	//     preferredSpacing: null, // or a fixed value
	//     stackThreshold: 0.7, // Custom threshold
	// },
};

/*
------------------------------------------------------------------------
Get the spacing configuration for a product.

@param {string} productId - The product ID (e.g., 'LN', 'BL', 'HX')
@return {object} The spacing configuration merged with defaults
------------------------------------------------------------------------
*/
const getProductSpacingConfig = (productId) => {
	const defaultConfig = PRODUCT_SPACING_CONFIG.default;
	const productConfig = PRODUCT_SPACING_CONFIG[productId] || {};

	return {
		...defaultConfig,
		...productConfig,
	};
};

/*
------------------------------------------------------------------------
Convert millimeters to canvas pixels.

@param {number} mm - Value in millimeters
@param {number} gridStep - Current grid step (pixels per meter)
@return {number} Value in pixels
------------------------------------------------------------------------
*/
const mmToPixels = (mm, gridStep) => {
	return (mm / 1000) * gridStep;
};

/*
------------------------------------------------------------------------
Convert canvas pixels to millimeters.

@param {number} pixels - Value in pixels
@param {number} gridStep - Current grid step (pixels per meter)
@return {number} Value in millimeters
------------------------------------------------------------------------
*/
const pixelsToMm = (pixels, gridStep) => {
	return (pixels / gridStep) * 1000;
};

/*
------------------------------------------------------------------------
Calculate the spacing between products for canvas rendering.

For products with a preferred spacing, returns that fixed value.
For others, dynamically calculates based on available space.

@param {string} productId - The product ID (e.g., 'LN', 'BL')
@param {number} numProducts - Number of products to render
@param {number} availableWidthPx - Available width in pixels
@param {number} productWidthPx - Width of each product in pixels
@param {number} gridStep - Current grid step (pixels per meter)
@return {number} Spacing in pixels
------------------------------------------------------------------------
*/
const calculateProductSpacing = (
	productId,
	numProducts,
	availableWidthPx,
	productWidthPx,
	gridStep,
) => {
	const config = getProductSpacingConfig(productId);

	// Convert config values to pixels
	const minSpacingPx = mmToPixels(config.minSpacing, gridStep);
	const maxSpacingPx = mmToPixels(config.maxSpacing, gridStep);

	// If product has a preferred spacing, use it (clamped to min/max)
	if (config.preferredSpacing !== null) {
		const preferredPx = mmToPixels(config.preferredSpacing, gridStep);
		return Math.max(minSpacingPx, Math.min(maxSpacingPx, preferredPx));
	}

	// Dynamic calculation for products without preferred spacing
	if (numProducts <= 1) {
		return minSpacingPx;
	}

	const totalProductWidth = numProducts * productWidthPx;
	const totalSpace = availableWidthPx - totalProductWidth;
	let spacing = totalSpace / (numProducts - 1);

	// Clamp to min/max
	spacing = Math.max(minSpacingPx, Math.min(maxSpacingPx, spacing));

	return spacing;
};

/*
------------------------------------------------------------------------
Calculate the optimal number of rows/columns for product layout.

Uses the stackThreshold to determine if products should be kept in a
single row/column or broken into multiple rows/columns.

@param {string} productId - The product ID (e.g., 'LN', 'BL')
@param {number} numProducts - Number of products to render
@param {number} availableSpacePx - Available space in pixels (room dimension)
@param {number} productDimensionPx - Product dimension in pixels (along the stacking axis)
@param {number} spacingPx - Spacing between products in pixels
@return {object} { itemsPerRow, numRows } - Optimal grid configuration
------------------------------------------------------------------------
*/
const calculateOptimalGrid = (
	productId,
	numProducts,
	availableSpacePx,
	productDimensionPx,
	spacingPx,
) => {
	const config = getProductSpacingConfig(productId);
	const stackThreshold = config.stackThreshold;

	// Calculate total width if all products in single row
	const singleRowWidth =
		numProducts * productDimensionPx + (numProducts - 1) * spacingPx;

	// Calculate the threshold width (e.g., 80% of available space)
	const thresholdWidth = availableSpacePx * stackThreshold;

	// If all products fit within threshold, keep as single row
	if (singleRowWidth <= thresholdWidth) {
		return {
			itemsPerRow: numProducts,
			numRows: 1,
		};
	}

	// Calculate maximum products that can physically fit in one row
	const maxItemsPerRow = Math.max(
		1,
		Math.floor(
			(availableSpacePx + spacingPx) / (productDimensionPx + spacingPx),
		),
	);

	// Calculate how many items fit within the threshold
	let itemsWithinThreshold = 1;
	for (let i = numProducts; i >= 1; i--) {
		const rowWidth = i * productDimensionPx + (i - 1) * spacingPx;
		if (rowWidth <= thresholdWidth) {
			itemsWithinThreshold = i;
			break;
		}
	}

	// Use the threshold-based count, but don't exceed physical maximum
	const itemsPerRow = Math.min(itemsWithinThreshold, maxItemsPerRow);

	// Calculate number of rows needed
	const numRows = Math.ceil(numProducts / itemsPerRow);

	// Balance the rows - prefer even distribution
	const balancedItemsPerRow = Math.ceil(numProducts / numRows);

	return {
		itemsPerRow: balancedItemsPerRow,
		numRows: numRows,
	};
};

/*
------------------------------------------------------------------------
Exports
------------------------------------------------------------------------
*/
export const CanvasUtils = {
	PRODUCT_SPACING_CONFIG,
	getProductSpacingConfig,
	mmToPixels,
	pixelsToMm,
	calculateProductSpacing,
	calculateOptimalGrid,
};
